// Maps the Organization Portal API models (src/model/emailMarketing) to the view models the
// Email Marketing screens already use (./types), so the UI doesn't change when it reads real data.
import {
    AudienceSelection,
    CampaignAudience,
    EmailMarketingKpis,
    EmailMarketingLocation,
    EmailMarketingSender,
    EmailMarketingTemplateOption,
    OrganizationCampaign,
    OrganizationCampaignInput
} from 'model/emailMarketing';
import { CampaignDraft, EmailCampaign, EmailLocation, EmailSender, EmailTemplate, RecipientType } from './types';
import { formatNumber, formatPercent } from './utils';

// Client groups are clients filtered by tag (criteria by_tag).
const RECIPIENT_TYPES: Record<CampaignAudience, RecipientType> = {
    both: 'all_profiles',
    leads: 'leads',
    customers: 'all_clients'
};

export const toAudience = (recipientType: RecipientType): CampaignAudience =>
    recipientType === 'all_profiles' ? 'both' : recipientType === 'leads' ? 'leads' : 'customers';

// Locations are grouped by state in the pickers.
export const toEmailLocation = (location: EmailMarketingLocation): EmailLocation => ({
    id: location.id,
    name: location.name,
    region: location.state ?? '',
    city: location.city ?? ''
});

export const toEmailSender = (sender: EmailMarketingSender): EmailSender => ({
    locationId: sender.locationId,
    status: sender.status,
    email: sender.senderEmail,
    verifiedAt: sender.verifiedAt,
    lastError: sender.lastError
});

// Until the senders load, the location picker data says which locations can send.
export const toEmailSenderFromLocation = (location: EmailMarketingLocation): EmailSender => ({
    locationId: location.id,
    status: location.verifiedSender ? 'verified' : location.senderEmail ? 'pending' : 'unconfigured',
    email: location.senderEmail,
    verifiedAt: null,
    lastError: null
});

// Gallery categories are master data ids; templateCategoryLabel turns them into labels.
export const toEmailTemplate = (template: EmailMarketingTemplateOption): EmailTemplate => ({
    id: template.id,
    name: template.name,
    category: template.category ?? '',
    source: template.system ? 'gallery' : 'location',
    locationId: template.locationId,
    subjectDefault: template.subjectDefault ?? '',
    html: template.html ?? '',
    updatedAt: ''
});

// Draft audience in the API shape: exclude mode ('all' + excluded) or include mode (picked ids).
export const toAudienceSelection = (draft: CampaignDraft): AudienceSelection => ({
    audience: toAudience(draft.recipientType),
    locationIds: draft.locationIds,
    criteria: draft.recipientType === 'client_groups' ? 'by_tag' : 'all',
    tags: draft.recipientType === 'client_groups' ? draft.tags : [],
    excludeIds: draft.selectedCustomerIds === 'all' ? draft.excludedCustomerIds : [],
    customerIds: draft.selectedCustomerIds === 'all' ? null : draft.selectedCustomerIds
});

export const toCampaignInput = (draft: CampaignDraft): OrganizationCampaignInput => ({
    name: draft.name.trim(),
    subject: draft.subject.trim(),
    previewText: draft.previewText.trim(),
    audience: toAudience(draft.recipientType),
    ...(draft.templateId !== null && { templateId: draft.templateId }),
    locationIds: draft.locationIds,
    selection: toAudienceSelection(draft)
});

export const toEmailCampaign = (campaign: OrganizationCampaign): EmailCampaign => ({
    id: campaign.id,
    name: campaign.name,
    subject: campaign.subject,
    previewText: campaign.previewText ?? '',
    status: campaign.status,
    recipientType: campaign.selection.criteria === 'by_tag' ? 'client_groups' : RECIPIENT_TYPES[campaign.audience],
    tags: campaign.selection.tags,
    selectedCustomerIds: campaign.selection.customerIds ?? 'all',
    excludedCustomerIds: campaign.selection.excludeIds,
    locationIds: campaign.locations.map(location => location.locationId),
    templateId: campaign.templateId,
    recipients: campaign.totals?.recipients ?? 0,
    delivered: campaign.totals?.delivered ?? 0,
    // No rate until something was delivered (drafts show "—", not 0%).
    openRate: campaign.totals?.delivered ? campaign.totals.openRate : null,
    clickRate: campaign.totals?.delivered ? campaign.totals.clickRate : null,
    createdBy: campaign.createdBy ?? '',
    sentAt: campaign.sentAt,
    updatedAt: campaign.updatedAt,
    results: campaign.locations
        .filter(location => location.counts)
        .map(location => ({
            locationId: location.locationId,
            recipients: location.counts?.recipients ?? 0,
            delivered: location.counts?.delivered ?? 0,
            opens: location.counts?.opens ?? 0,
            clicks: location.counts?.clicks ?? 0
        }))
});

export interface KpiCard {
    id: string;
    label: string;
    value: string;
    hint?: string;
    delta?: number;
}

// "Campaigns sent" is counted from the list over the same window as the API KPIs.
export const toKpiCards = (kpis: EmailMarketingKpis | undefined, campaigns: Array<EmailCampaign>): Array<KpiCard> => {
    const days = kpis?.days ?? 30;
    const hint = `Last ${days} days`;
    const since = Date.now() - days * 24 * 60 * 60 * 1000;
    const sentCampaigns = campaigns.filter(
        campaign => campaign.status === 'sent' && campaign.sentAt && new Date(campaign.sentAt).getTime() >= since
    ).length;

    return [
        { id: 'sent', label: 'Campaigns sent', value: formatNumber(sentCampaigns), hint },
        { id: 'delivered', label: 'Emails sent', value: kpis ? formatNumber(kpis.emailsSent) : '—', hint },
        {
            id: 'open',
            label: 'Avg. open rate',
            value: formatPercent(kpis?.emailsSent ? kpis.averageOpenRate : null),
            hint
        },
        {
            id: 'click',
            label: 'Avg. click rate',
            value: formatPercent(kpis?.emailsSent ? kpis.averageClickRate : null),
            hint
        }
    ];
};
